import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { Button } from '../components/ui/Button';
import supabase from '../supabase/supabase';
import InteractiveBentoGallery from '../components/ui/interactive-bento-gallery';

const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780696106/Remove_Background_wepspn.png";

const galleryItems = [
  {
    id: 1,
    type: "image",
    title: "Precision Fades",
    desc: "Expert blending and sharp details.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224168/WhatsApp_Image_2026-07-03_at_18.56.00_3_k1ccpa.jpg",
    span: "col-span-2 row-span-2 md:col-span-2 md:row-span-3",
  },
  {
    id: 2,
    type: "image",
    title: "Classic Styling",
    desc: "Timeless looks for modern gentlemen.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224168/WhatsApp_Image_2026-07-03_at_18.56.55_goeqnt.jpg",
    span: "col-span-1 row-span-1 md:col-span-1 md:row-span-2",
  },
  {
    id: 3,
    type: "image",
    title: "Beard Grooming",
    desc: "Sculpting and conditioning.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224168/WhatsApp_Image_2026-07-03_at_18.56.00_bqlpfp.jpg",
    span: "col-span-1 row-span-1 md:col-span-1 md:row-span-2",
  },
  {
    id: 4,
    type: "image",
    title: "Kids Haircuts",
    desc: "Patient, gentle, and stylish.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.56.00_2_l7us4y.jpg",
    span: "col-span-1 row-span-2 md:col-span-1 md:row-span-2",
  },
  {
    id: 5,
    type: "image",
    title: "At-Home Convenience",
    desc: "We bring the barbershop to you.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.49.22_dmmpn1.jpg",
    span: "col-span-2 row-span-2 md:col-span-2 md:row-span-3",
  },
  {
    id: 6,
    type: "image",
    title: "Hot Towel Shave",
    desc: "Relaxing and smooth finish.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.56.00_4_mqo3ni.jpg",
    span: "col-span-1 row-span-1 md:col-span-1 md:row-span-2",
  },
  {
    id: 7,
    type: "image",
    title: "Line Ups",
    desc: "Crisp and clean edges.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.50.49_vw6udk.jpg",
    span: "col-span-1 row-span-1 md:col-span-1 md:row-span-2",
  },
  {
    id: 8,
    type: "image",
    title: "Hair Coloring",
    desc: "Vibrant and natural shades.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.55.59_o8hl24.jpg",
    span: "col-span-2 row-span-1 md:col-span-2 md:row-span-2",
  },
  {
    id: 9,
    type: "image",
    title: "Premium Tools",
    desc: "Sanitized and top-tier equipment.",
    url: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783224167/WhatsApp_Image_2026-07-03_at_18.56.00_1_h0e1is.jpg",
    span: "col-span-1 row-span-1 md:col-span-2 md:row-span-2",
  }
];

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles_1740423033529')
          .select('full_name, role')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          setUserProfile(profile);
        } else {
          setUserProfile({ full_name: session.user.email.split('@')[0], role: 'customer' });
        }
      }
    };
    checkUser();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const navigateToDashboard = () => {
    if (!userProfile) return;
    if (userProfile.role === 'admin') navigate('/admin/dashboard');
    else if (userProfile.role === 'barber') navigate('/barber/dashboard');
    else navigate('/app');
  };

  const faqs = [
    {
      question: "How do I book a barber?",
      answer: "Simply sign up, select your district, choose a barber, select your service, and pick a time. Your barber will arrive at your location."
    },
    {
      question: "How do I pay for the service?",
      answer: "Currently, we accept cash payments directly to the barber after your grooming session is completed. Card payments are in development."
    },
    {
      question: "Can I cancel my appointment?",
      answer: "Yes, you can cancel your appointment from your dashboard. We kindly ask that you provide a reason so your barber can adjust their schedule."
    },
    {
      question: "Is BookOTG available everywhere?",
      answer: "We are currently operating exclusively in Sri Lanka. As we onboard more elite barbers, our district availability is constantly expanding."
    }
  ];

  return (
    <div className="min-h-screen bg-brand-light font-sans flex flex-col">
      {/* Floating Glassy Navbar */}
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-7xl bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-full z-50 px-4 sm:px-8 py-3 flex justify-between items-center transition-all">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src={LOGO_URL} alt="BookOTG" className="h-10 sm:h-12 w-auto object-contain" />
        </div>
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-brand-primary font-bold transition-colors">How it Works</button>
          <button onClick={() => scrollToSection('gallery')} className="text-gray-600 hover:text-brand-primary font-bold transition-colors">Gallery</button>
          <button onClick={() => scrollToSection('for-barbers')} className="text-gray-600 hover:text-brand-primary font-bold transition-colors">For Barbers</button>
          <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-brand-primary font-bold transition-colors">FAQ</button>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {userProfile ? (
            <Button className="rounded-full shadow-lg font-bold text-sm px-6" onClick={navigateToDashboard}>
              Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" className="text-sm px-2 sm:px-4 font-bold text-gray-700" onClick={() => navigate('/login')}>Login</Button>
              <Button className="rounded-full shadow-lg font-bold text-sm px-4 sm:px-6" onClick={() => navigate('/login?signup=true')}>Sign up</Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24 flex flex-col lg:flex-row items-center gap-16">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex-1 text-center lg:text-left"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary/10 text-brand-primary font-black text-[10px] mb-8 border border-brand-primary/20 uppercase tracking-[0.2em]">
            <SafeIcon name="Star" className="fill-current text-sm" />
            Premium Grooming On Demand
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight text-brand-dark mb-6 leading-[1.1]">
            Book a Barber To Your <span className="text-gradient">Doorstep</span>
          </h1>
          <p className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
            Elite barbers, professional equipment, and the comfort of your own home. Experience the future of grooming with BookOTG.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-12">
            {userProfile ? (
              <div className="flex flex-col items-center lg:items-start gap-4 w-full">
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-50 border border-green-100 text-green-700 font-bold text-sm">
                  <SafeIcon name="CheckCircle" className="text-green-500" />
                  You are already logged in as {userProfile.full_name}
                </div>
                <Button size="lg" className="w-full sm:w-auto hover:bg-brand-accent transition-colors rounded-2xl py-4 px-8 text-lg font-black" onClick={navigateToDashboard}>
                  Go to Dashboard
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row w-full sm:w-auto items-center gap-4">
                <Button size="lg" className="w-full sm:w-auto hover:bg-brand-accent transition-colors rounded-2xl py-4 px-8 text-lg font-black" onClick={() => navigate('/login?signup=true')}>
                  Sign up as a customer
                </Button>
                <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-2xl py-4 px-8 text-lg font-black bg-white" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center lg:justify-start gap-8 border-t border-gray-200 pt-8">
            <div>
              <p className="text-4xl font-black text-brand-primary">500+</p>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Expert Barbers</p>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div>
              <p className="text-4xl font-black text-brand-accent">10K+</p>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Happy Clients</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="flex-1 w-full max-w-lg lg:max-w-none relative"
        >
          <div className="absolute inset-0 bg-brand-primary/10 rounded-[3rem] transform rotate-3 scale-105 -z-10 blur-xl"></div>
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white">
            <img 
              src="https://images.unsplash.com/photo-1585747860715-2ba37e788b70?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
              alt="Barber cutting hair" 
              className="w-full h-auto object-cover aspect-[4/3] rounded-[2rem]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/20 to-transparent flex items-end p-8">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 flex items-center gap-4 w-full text-white shadow-2xl">
                <div className="w-14 h-14 rounded-2xl bg-brand-accent flex items-center justify-center flex-shrink-0 shadow-lg shadow-brand-accent/30">
                  <SafeIcon name="MapPin" className="text-brand-dark text-2xl" />
                </div>
                <div>
                  <p className="font-black text-lg tracking-tight">Live Tracking</p>
                  <p className="text-sm text-white/80 font-medium">Your barber is 5 minutes away</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-brand-dark mb-4">How It Works</h2>
            <p className="text-gray-500 font-medium max-w-2xl mx-auto">Three simple steps to experience professional grooming without leaving your home.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                icon: 'Search',
                title: "1. Find & Book",
                desc: "Select a top-rated barber in your district and choose your preferred service."
              },
              {
                icon: 'MapPin',
                title: "2. Barber Arrives",
                desc: "Your professional barber comes directly to your location with all necessary equipment."
              },
              {
                icon: 'Star',
                title: "3. Look Sharp",
                desc: "Enjoy a premium grooming experience and pay securely after the service is completed."
              }
            ].map((step, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="flex flex-col items-center text-center p-8 rounded-[2.5rem] bg-gray-50 border border-gray-100 hover:border-brand-primary/30 transition-colors group"
              >
                <div className="w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                  <SafeIcon name={step.icon} className="text-3xl text-brand-primary" />
                </div>
                <h3 className="text-xl font-black text-brand-dark mb-3">{step.title}</h3>
                <p className="text-gray-500 font-medium leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-gray-50 relative border-y border-gray-100">
        <InteractiveBentoGallery
          mediaItems={galleryItems}
          title="Our Work in Action"
          description="Swipe, drag, and explore the premium grooming experiences delivered by our elite barbers."
        />
      </section>

      {/* For Barbers Section */}
      <section id="for-barbers" className="py-24 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-primary rounded-full blur-[120px] opacity-20 translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1"
            >
              <h2 className="text-4xl lg:text-5xl font-black mb-6 tracking-tight">Are you a Barber? Join the Elite.</h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Transform your career with BookOTG. Be your own boss, set your own schedule, and reach thousands of clients across Sri Lanka looking for premium home-service grooming.
              </p>
              
              <ul className="space-y-4 mb-10">
                {['Keep 85% of your earnings', 'Flexible working hours', 'Free marketing and client acquisition', 'Manage bookings via our app'].map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 font-bold">
                    <SafeIcon name="CheckCircle" className="text-brand-accent" />
                    {benefit}
                  </li>
                ))}
              </ul>

              <Button onClick={() => navigate('/login?signup=true&role=barber')} className="bg-white text-brand-dark hover:bg-gray-100 px-8 py-4 rounded-2xl text-lg font-black shadow-xl shadow-white/10">
                Start Earning Today
              </Button>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1 w-full"
            >
              <div className="bg-white/5 border border-white/10 p-8 rounded-[2.5rem] backdrop-blur-xl shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Professional Barber" 
                  className="w-full h-80 object-cover rounded-[2rem] shadow-inner mb-8"
                />
                
                <div className="flex justify-between items-center text-center">
                  <div>
                    <p className="text-2xl font-black text-white">Rs. 150K+</p>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">Avg. Monthly Potential</p>
                  </div>
                  <div className="w-px h-10 bg-white/10"></div>
                  <div>
                    <p className="text-2xl font-black text-brand-accent">100%</p>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">Your Schedule</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-brand-dark mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-500 font-medium">Everything you need to know about BookOTG.</p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm transition-all duration-300"
              >
                <button 
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center font-bold text-brand-dark hover:bg-gray-50 focus:outline-none"
                >
                  {faq.question}
                  <SafeIcon 
                    name="ChevronDown" 
                    className={`text-gray-400 transition-transform duration-300 ${openFaq === index ? 'rotate-180 text-brand-primary' : ''}`} 
                  />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-5 text-gray-500 font-medium leading-relaxed"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <img src={LOGO_URL} alt="BookOTG" className="h-12 w-auto object-contain mb-6" />
              <p className="text-gray-500 font-medium max-w-sm leading-relaxed mb-6">
                Bringing the barbershop experience directly to your home. Premium grooming, elite professionals, unmatched convenience.
              </p>
            </div>
            
            <div>
              <h4 className="font-black text-brand-dark mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/privacypolicy" className="text-gray-500 hover:text-brand-primary font-medium transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-500 hover:text-brand-primary font-medium transition-colors">Terms of Service</Link></li>
                <li><Link to="/request-deletion" className="text-red-400 hover:text-red-600 font-bold transition-colors">Account Deletion</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-black text-brand-dark mb-4">Contact</h4>
              <ul className="space-y-3">
                <li><Link to="/contact" className="text-gray-500 hover:text-brand-primary font-medium transition-colors">Get in touch</Link></li>
                <li className="text-gray-500 font-medium flex items-center gap-2 mt-4">
                  <SafeIcon name="Mail" className="text-brand-primary" />
                  help@bookotg.com
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-400 font-bold tracking-widest uppercase text-center md:text-left">
              &copy; 2026 BookOTG Platform is Managed by Fesy Marketing Pvt Ltd Sri Lanka
            </p>
            <div className="flex gap-4">
              {['Facebook', 'Instagram', 'Twitter'].map((social, i) => (
                <a key={i} href="#" className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-brand-primary hover:text-white transition-all">
                  <SafeIcon name={social} className="text-sm" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}